import { useEffect, useRef, type FocusEvent } from 'react';

/** Reveal only this strip; scrollIntoView would also move the learning viewport. */
function revealLink(nav: HTMLElement, link: HTMLElement | null) {
  if (!link || nav.clientWidth === 0) return;
  const strip = nav.getBoundingClientRect();
  const item = link.getBoundingClientRect();
  const inset = 4;
  const delta =
    item.left < strip.left + inset
      ? item.left - strip.left - inset
      : Math.max(0, item.right - strip.right + inset);
  if (delta !== 0) nav.scrollTo({ left: nav.scrollLeft + delta, behavior: 'auto' });
}

/** Shared by the Study header and global Learn dock; never scroll page content. */
export function useNavigationVisibility<T extends HTMLElement = HTMLElement>(selectionKey: string) {
  const navigation = useRef<T>(null);
  useEffect(() => {
    const nav = navigation.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]');
    const revealActive = () => revealLink(nav, active);
    revealActive();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', revealActive);
      return () => window.removeEventListener('resize', revealActive);
    }
    const resize = new ResizeObserver(revealActive);
    resize.observe(nav);
    if (active) resize.observe(active);
    return () => resize.disconnect();
  }, [selectionKey]);
  const revealFocusedLink = (event: FocusEvent<T>) =>
    revealLink(event.currentTarget, event.target.closest('a, button'));
  return { navigation, revealFocusedLink };
}
